"use client"

import { useRef } from "react"
import { Icon } from "@/shared/ui/icon"
import { useAtlasReveal } from "./useAtlasReveal"

const securityRevealTargets = [
  { selector: ".security-control-copy", y: 32 },
  { selector: ".security-control-panel", y: 34, scale: 0.97 },
  { selector: ".security-log-row", x: 20, y: 0, stagger: 70 },
] as const

type PermissionState = "allowed" | "blocked"

type PermissionRow = {
  source: string
  scope: string
  state: PermissionState
  note: string
}

type EvidenceItem = {
  title: string
  text: string
  marker: string
  icon: string
}

const stateLabels: Record<PermissionState, string> = {
  allowed: "Разрешено",
  blocked: "Не включено",
}

const stateClasses: Record<PermissionState, string> = {
  allowed: "border-emerald-400/25 bg-emerald-400/10 text-emerald-300",
  blocked: "border-white/10 bg-white/[0.045] text-slate-400",
}

const permissionRows: readonly PermissionRow[] = [
  {
    source: "Metabase",
    scope: "Read dashboards",
    state: "allowed",
    note: "метрики и отклонения из подключённого источника",
  },
  {
    source: "Mattermost",
    scope: "Read messages",
    state: "allowed",
    note: "обсуждения и блокеры из рабочего канала",
  },
  {
    source: "Nextcloud",
    scope: "Read links",
    state: "allowed",
    note: "командные материалы и ссылки, к которым дали доступ",
  },
  {
    source: "CRM / Tasks",
    scope: "Update records",
    state: "blocked",
    note: "write-действия не заявлены как готовый сценарий Atlas",
  },
  {
    source: "Данные",
    scope: "Delete",
    state: "blocked",
    note: "удаление не показываем как доступную функцию",
  },
] as const

const actionLog = [
  {
    time: "09:00",
    title: "Принял задачу",
    detail: "собрать управленческий бриф за неделю",
  },
  {
    time: "09:01",
    title: "Проверил разрешённые источники",
    detail: "Metabase, Mattermost, Nextcloud",
  },
  {
    time: "09:03",
    title: "Открыл веб-поиск",
    detail: "provider tool, максимум 4 обращения",
  },
] as const

const evidenceItems: readonly EvidenceItem[] = [
  {
    title: "Только подключённые источники",
    text: "Секция опирается на текущие интеграции Atlas: Metabase, Mattermost и Nextcloud.",
    marker: "Allowed sources",
    icon: "database-bold-duotone",
  },
  {
    title: "Видимый ход работы",
    text: "В интерфейсе уже есть шаги агента и tool activity: пользователь видит, что именно выполнялось.",
    marker: "Action trace",
    icon: "history-2-bold-duotone",
  },
] as const

function PermissionIcon({ state }: { state: PermissionState }) {
  if (state === "blocked") {
    return <Icon name="close-circle-linear" className="size-4" />
  }

  return <Icon name="check-read-linear" className="size-4" />
}

export default function SecurityControlSection() {
  const sectionRef = useRef<HTMLElement>(null)

  useAtlasReveal(sectionRef, securityRevealTargets)

  return (
    <section
      ref={sectionRef}
      id="control"
      aria-labelledby="control-heading"
      className="relative overflow-x-hidden border-y border-slate-200/70 bg-[#f7f6f1] py-20 dark:border-white/10 dark:bg-[#0d0f12] sm:py-28 lg:py-36"
    >
      <div className="pointer-events-none absolute -left-44 top-20 size-[34rem] rounded-full bg-emerald-500/10 blur-3xl dark:bg-orange-500/15" />
      <div className="pointer-events-none absolute -right-44 bottom-10 size-[30rem] rounded-full bg-orange-500/10 blur-3xl dark:bg-emerald-400/10" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          <div className="security-control-copy">
            <h2
              id="control-heading"
              className="max-w-4xl text-headline text-slate-950 dark:text-white"
            >
              Агент работает самостоятельно. Контроль остаётся у вас.
            </h2>
            <p className="mt-5 max-w-2xl text-lead text-slate-600 dark:text-slate-300">
              Atlas показывает автономность через понятные границы: какие источники
              подключены, какие действия не входят в текущий сценарий и что уже выполнено.
            </p>

            <div className="mt-8 grid grid-flow-dense gap-4 lg:grid-cols-2">
              {evidenceItems.map((item) => (
                <article
                  key={item.title}
                  className="group min-h-56 overflow-hidden rounded-xl border border-slate-200 bg-white p-5 transition-colors duration-300 hover:border-slate-300 dark:border-white/10 dark:bg-white/[0.035] dark:hover:border-white/20 sm:p-6"
                >
                  <div className="grid h-full grid-rows-[auto_3rem_auto_1fr]">
                    <div className="flex items-center justify-between gap-4">
                      <span className="type-overline rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-slate-500 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-400">
                        {item.marker}
                      </span>
                      <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition-transform duration-700 ease-out group-hover:scale-105 dark:bg-white/10 dark:text-white">
                        <Icon name={item.icon} className="size-5" />
                      </span>
                    </div>
                    <div aria-hidden="true" />
                    <h3 className="max-w-sm self-start text-title text-slate-950 dark:text-white">
                      {item.title}
                    </h3>
                    <p className="mt-4 max-w-md self-start text-base text-slate-600 dark:text-slate-400">
                      {item.text}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="security-control-panel overflow-hidden rounded-2xl border border-white/15 bg-[#16181d] text-white shadow-2xl shadow-slate-950/20">
            <div className="grid gap-3 border-b border-white/10 bg-[#1c1f26] px-4 py-4 sm:px-6">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-100">
                  Права доступа сценария
                </p>
                <p className="mt-1 font-mono text-2xs text-slate-400">
                  sales-brief · connected sources only · visible action trace
                </p>
              </div>
              <span className="inline-flex w-fit items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-xs font-medium text-emerald-200">
                <Icon name="shield-check-linear" className="size-4" />
                Границы заданы
              </span>
            </div>

            <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="rounded-xl border border-white/10 bg-white/[0.035] p-3 sm:p-4">
                <div className="hidden grid-cols-[minmax(6rem,0.8fr)_minmax(7rem,0.9fr)_auto] gap-3 border-b border-white/10 px-1 pb-3 type-overline text-slate-500 sm:grid">
                  <span>Источник</span>
                  <span>Действие</span>
                  <span className="text-right">Статус</span>
                </div>

                <div className="divide-y divide-white/10">
                  {permissionRows.map((row) => (
                    <div
                      key={`${row.source}-${row.scope}`}
                      className="grid gap-3 px-1 py-4 sm:grid-cols-[minmax(6rem,0.8fr)_minmax(7rem,0.9fr)_auto]"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">
                          {row.source}
                        </p>
                        <p className="mt-1 line-clamp-2 text-xs text-slate-400">
                          {row.note}
                        </p>
                      </div>
                      <p className="min-w-0 break-words font-mono text-xs text-slate-300">
                        {row.scope}
                      </p>
                      <span
                        className={`inline-flex h-fit w-fit min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-2xs font-medium sm:justify-end sm:justify-self-end ${stateClasses[row.state]}`}
                      >
                        <PermissionIcon state={row.state} />
                        <span>{stateLabels[row.state]}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="security-log rounded-xl border border-white/10 bg-[#101216] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">Журнал действий</p>
                    <p className="mt-1 text-xs text-slate-500">видимый ход агента</p>
                  </div>
                  <Icon name="history-2-bold-duotone" className="size-6 text-slate-300" />
                </div>

                <ol className="mt-5 space-y-3">
                  {actionLog.map((item) => (
                    <li
                      key={`${item.time}-${item.title}`}
                      className="security-log-row grid grid-cols-[3.25rem_minmax(0,1fr)] gap-3 rounded-lg border border-white/8 bg-white/[0.035] p-3"
                    >
                      <span className="type-numeric text-2xs font-medium text-emerald-300">
                        {item.time}
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-white">{item.title}</p>
                        <p className="mt-1 text-xs text-slate-400">{item.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
